import { ASYNC_TASK_STATUS, ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";

export type WizardStep =
  | "setup"
  | "page-picker"
  | "analysing"
  | "questions"
  | "building"
  | "context-view";

export type BoardContextStatus = "in_progress" | "completed";

export interface TrackedTaskIds {
  questionsTaskId?: string | null;
  contextTaskId?: string | null;
}

interface AsyncTaskLike {
  id: number | string;
  type: number;
  status: string;
  meta_data?: { board_id?: number | string };
}

const byIdDesc = (a: { id: number | string }, b: { id: number | string }) =>
  Number(b.id) - Number(a.id);

export const taskMatchesBoard = (task: AsyncTaskLike, boardId: number | undefined) =>
  boardId != null && Number(task.meta_data?.board_id) === Number(boardId);

export const hasDraftQuestionsReady = (record: BoardContext | null) =>
  record?.draftQuestions !== null &&
  Array.isArray(record?.draftQuestions?.questions) &&
  (record?.draftQuestions?.questions.length ?? 0) > 0;

/** Context view is ready when the board context pipeline has finished (persisted status). */
export const isContextViewReady = (record: BoardContext | null) =>
  record?.status === "completed";

export const boardTasksForBoard = (
  tasks: AsyncTaskLike[],
  type: number,
  boardId: number | undefined
) =>
  tasks.filter((t) => t.type === type && taskMatchesBoard(t, boardId)).sort(byIdDesc);

export const resolveTrackedTask = (tasks: AsyncTaskLike[], trackedId: string | null) => {
  if (!tasks.length) return undefined;
  if (trackedId) {
    return tasks.find((t) => String(t.id) === trackedId) ?? tasks[0];
  }
  return tasks[0];
};

/**
 * True when the latest context-generation cycle for this board has finished (no pending task).
 * Ignores older completed context tasks if a newer questions-generation task exists (source re-run).
 */
export const isContextGenerationDone = (
  cTasks: AsyncTaskLike[],
  qTasks: AsyncTaskLike[],
  tracked?: TrackedTaskIds
): boolean => {
  if (cTasks.some((t) => t.status === ASYNC_TASK_STATUS.PENDING)) return false;

  if (tracked?.contextTaskId) {
    const cTask = resolveTrackedTask(cTasks, tracked.contextTaskId);
    return cTask?.status === ASYNC_TASK_STATUS.COMPLETED;
  }

  const latestContext = cTasks[0];
  if (!latestContext || latestContext.status !== ASYNC_TASK_STATUS.COMPLETED) {
    return false;
  }

  const latestQuestions = qTasks[0];
  if (!latestQuestions) return true;

  return Number(latestContext.id) > Number(latestQuestions.id);
};

/**
 * Single source of truth for which wizard step to show.
 * Loaders follow pending async tasks; context-view when record or current context-gen is done.
 */
export const resolveWizardStep = (
  record: BoardContext | null,
  allTasks: AsyncTaskLike[],
  boardId: number | undefined,
  tracked?: TrackedTaskIds
): WizardStep => {
  if (!record || boardId == null) return "setup";

  const qTasks = boardTasksForBoard(
    allTasks,
    ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
    boardId
  );
  const cTasks = boardTasksForBoard(
    allTasks,
    ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
    boardId
  );

  const latestQuestionsTask = qTasks[0];

  if (cTasks.some((t) => t.status === ASYNC_TASK_STATUS.PENDING)) return "building";
  if (qTasks.some((t) => t.status === ASYNC_TASK_STATUS.PENDING)) return "analysing";

  if (record.status === "completed") return "context-view";

  if (isContextGenerationDone(cTasks, qTasks, tracked)) return "context-view";

  if (
    latestQuestionsTask?.status === ASYNC_TASK_STATUS.COMPLETED &&
    !qTasks.some((t) => t.status === ASYNC_TASK_STATUS.PENDING) &&
    !hasDraftQuestionsReady(record)
  ) {
    return "analysing";
  }

  if (hasDraftQuestionsReady(record)) return "questions";
  if ((record.selectedUrls ?? []).length > 0) return "page-picker";
  return "setup";
};
